import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertType,
  FilterOperation,
  IAssemblyComponentInformation,
  IAssemblyComponentInformationQueryResult,
  IAssemblyComponentInformationQueryVariables,
  IAssemblyReservationDetail,
  ILotAttributeConfig,
  ProductClass,
  ReportType,
} from '../../graphql/schema/p4types';
import Assembly from '../../graphql/entities/product/Assembly';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import { Spinner } from '@fluentui/react/lib/Spinner';
import { default as Form } from '../../components/connectedForm';
import HeaderBlock from '../../components/connectedForm/HeaderBlock';
import { Prompt, useHistory } from 'react-router';
import setIn from '../../components/connectedForm/utils/setIn';
import EditableDataGrid from '../../components/EditableDataGrid';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import Formatters from '../../components/grid-addons/formatters';
import { getLotAttributeRowMetaData } from '../../utils/LotAttributeValues';
import {
  ExtendedAssemblyReservation,
  ExtendedAssemblyReservationDetail,
  useAssemblyReservationFormActions,
} from '../../actions/product/AssemblyReservationFormAction';
import _ from 'lodash';
import BlurTextField from '../../components/connectedForm/BlurTextField';
import { useLazyQuery, useQuery } from '@apollo/client';
import AssemblyComponentInformationQuery from '../../graphql/queries/product/AssemblyComponentInformationQuery.graphql';
import Label from '../../components/connectedForm/Label';
import { Link } from 'react-router-dom';
import { BlurTextFieldType } from '../../components/connectedForm/BlurTextField/BlurTextField';
import HintIcon from '../../components/connectedForm/HintIcon';
import { cloneDeep } from '@apollo/client/utilities';
import { IFormatterProps } from '../../@types/workspaceTypes';
import { useP4ToastController } from '../system/NotificationsArea';
import { Tooltip } from '@fluentui/react-components';
import { Dialog, DialogType } from '@fluentui/react';
import { Button, SuccessButton } from '../../components/connectedForm/Button';
import { DialogBottomButtonContainer } from '../../components/modals/Styles';
import { AssemblyReservationDetailsContextType, useAssemblyTreeContext } from './AssemblyTreeContext';
import AssemblyReservationsQuery from '../../graphql/queries/product/AssemblyReservationsQuery.graphql';
import styled from 'styled-components';
import iconMixin from '../../styles/utils/iconMixin';
import { AssemblyPrintSource, useP4DocumentController } from '../../components/document/DocumentProvider';

const Icon = styled.div`
  ${iconMixin('48px')};
  color: #ffa000;
  padding: 10px 10px 0 0;
  float: left;
`;

const ReservationDataGridEmptyComponent = () => (
  <div className="no-results">
    <Icon>error_outline</Icon>
    <h4>Unable to Reserve</h4>
    <p>In order to reserve components to a sub-assembly, the sub-assembly must have an assignment</p>
  </div>
);

const LazyAssemblyWorkspace = React.lazy(() => import('../../views/product/AssemblyWorkspace'));

type SelectedComponentType =
  | {
      rowPath: string;
      component: IAssemblyComponentInformation;
    }
  | undefined;

type AssemblyReservationsType = { [key: string]: ExtendedAssemblyReservation } | undefined;

// the formatter needs to show the reserveAllQuantity if the user has clicked 'Reserve All'
// unless there is a manual reservation i.e. the user has overridden that value, if we are
// not reserving all then display the manual reservation
const ReservationQtyFormatter = (props: IFormatterProps<ExtendedAssemblyReservationDetail>) => {
  const { dependentValues } = props;
  const { reserveAll, selectedComponent, assemblyReservations } = dependentValues.options.formatterOptions;
  const reserveAllQuantity = dependentValues.rowData?.reserveAllQuantity ?? 0;
  const hasManualReservation = selectedComponentHasManualReservation(selectedComponent, assemblyReservations);

  const isTopLevel = selectedComponent.rowPath.split('.').length === 2;

  let quantity: number;

  if (hasManualReservation) {
    quantity = dependentValues.rowData?.quantity ?? 0;
  } else if (reserveAll && isTopLevel) {
    quantity = reserveAllQuantity;
  } else {
    quantity = dependentValues.rowData?.quantity ?? 0;
  }

  return (
    <Tooltip content={'Enter a negative number to un-reserve stock'} relationship={'label'}>
      <div>{quantity}</div>
    </Tooltip>
  );
};

//checks if there is a manual reservation for the selected component line
const selectedComponentHasManualReservation = (
  selectedComponent: SelectedComponentType,
  assemblyReservations: AssemblyReservationsType
) => {
  if (selectedComponent?.rowPath) {
    const selectedAssemblyReservations = assemblyReservations?.[selectedComponent.rowPath];
    return (
      selectedAssemblyReservations &&
      selectedAssemblyReservations.assemblyReservationDetails.find((reservationDetail) =>
        reservationDetail?.hasOwnProperty('quantity')
      )
    );
  }
  return false;
};

const AssemblyReservationForm = () => {
  const {
    reservationAssembly: assembly,
    rowPath,
    assemblyReservations,
    setAssemblyReservations,
    setUpdatedNodes,
    component,
    setAssemblyComponentInformation: assemblyComponentInformationSetter,
    reserveAll,
    setReserveAll,
    assignedAssembly,
    unassignedStatus,
    selectedComponent: selectedNode,
    setAssembly,
  } = useAssemblyTreeContext() as AssemblyReservationDetailsContextType;

  /* process props */
  const assemblyId = assembly.id;
  const nodeId = selectedNode?.nodeId;

  /* hooks */
  const handleQueryError = useComponentQueryErrorHandling();
  const history = useHistory();
  const { printDocumentSingle } = useP4DocumentController();

  /* state */
  // keeps track of all reservations loaded indexed by the selected component
  const [pickingModalOpen, setPickingModalOpen] = useState<boolean>(false);
  const [allowNavigation, setAllowNavigation] = useState<boolean>(true);
  const [cancelModalOpen, setCancelModalOpen] = useState<boolean>(false);

  const { dispatchSimpleToast } = useP4ToastController();

  /* Queries/Mutations */
  const { data: assemblyComponentInformation, refetch: refetchAssemblyComponentInformation } = useQuery<
    IAssemblyComponentInformationQueryResult,
    IAssemblyComponentInformationQueryVariables
  >(AssemblyComponentInformationQuery, {
    onError: handleQueryError,
    fetchPolicy: 'network-only',
    variables: {
      assemblyId: assemblyId,
      path: unassignedStatus ? rowPath : null,
      parentComponentProductId: unassignedStatus ? null : assembly?.product?.id,
    },
  });

  const [fetchAssemblyReservations, { loading }] = useLazyQuery<
    IAssemblyReservationsQueryResult,
    IAssemblyReservationsQueryVariables
  >(AssemblyReservationsQuery, {
    skip: !assemblyId || !selectedNode?.component?.id,
    onError: handleQueryError,
    onCompleted: (data) => {
      const newReservations = {
        [selectedComponent?.rowPath!]: data?.data as ExtendedAssemblyReservation,
        ...assemblyReservations,
      };
      setAssemblyReservations(newReservations);
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  const updateAssemblyReservations = (assemblyReservations) => {
    const reservationsWithQuantities = [];

    for (const reservationKey in assemblyReservations) {
      const key = reservationKey;
      const reservationsValues = assemblyReservations[key].assemblyReservationDetails.every(
        (reservationDetail) => !reservationDetail.hasOwnProperty('quantity')
      );

      const reservationQty = assemblyReservations[key].assemblyReservationDetails.reduce(
        (total, assemblyReservationDetail) => {
          const qty = Number(assemblyReservationDetail.quantity);
          return total + (isNaN(qty) ? 0 : qty);
        },
        0
      );
      if (!reservationsValues && reservationQty > 0) {
        reservationsWithQuantities.push(assemblyReservations[key].productComponent.id);
      }
    }
    setUpdatedNodes(reservationsWithQuantities);
    setAssemblyReservations(assemblyReservations);
  };

  useEffect(() => {
    if (!assembly || !selectedNode) {
      return;
    }
    fetchAssemblyReservations({
      variables: {
        assemblyId: selectedNode?.assembly?.id ?? assembly.id,
        productComponentId: selectedNode?.nodeId,
      },
    });
  }, [assembly, selectedNode]);

  const selectedComponent = useMemo(() => {
    return {
      rowPath: rowPath,
      component: component,
    };
  }, [rowPath, component]);

  const selectedComponentInformation = useMemo(() => {
    const components = assemblyComponentInformation?.data?.filter((info) => info.productComponentId === nodeId);
    if (components) {
      const component = components.pop();
      return component;
    }
    return null;
  }, [assemblyComponentInformation, selectedComponent]);

  useEffect(() => {
    assemblyComponentInformationSetter(selectedComponentInformation);
  }, [selectedComponentInformation]);

  // updates any assembly reservations we have created to have a quantity equal to the reserve all quantity
  const reserveAllHandler = () => {
    setReserveAll(true);
    // take a clone otherwise useState won't pick up the change
    const updatedAssemblyReservations = cloneDeep(assemblyReservations);

    for (const componentId in updatedAssemblyReservations) {
      if (updatedAssemblyReservations?.hasOwnProperty(componentId)) {
        updatedAssemblyReservations[componentId].assemblyReservationDetails = updatedAssemblyReservations[
          componentId
        ].assemblyReservationDetails.map((reservationDetail) => ({
          ...reservationDetail,
          quantity: reservationDetail?.reserveAllQuantity ?? 0,
        }));
      }
    }
    updateAssemblyReservations(updatedAssemblyReservations);
    dispatchSimpleToast('Reserve All', 'Reserve All Completed', AlertType.Info);
  };

  const ASSEMBLY_RESERVATION_COLUMNS = useMemo((): Array<IEditableDataGridColumnDef> => {
    // @ts-ignore
    const attributeColumns: Array<IEditableDataGridColumn> =
      assemblyReservations &&
      selectedComponent?.rowPath !== undefined &&
      assemblyReservations![selectedComponent.rowPath]
        ? (
            assemblyReservations![selectedComponent.rowPath].productComponent!.component!
              .lotAttributes! as Array<ILotAttributeConfig>
          ).map((lotAttributeConfig) => {
            return {
              name: lotAttributeConfig.title,
              key: 'stockLot.lotAttributeValues',
              formatter: Formatters.LotAttributeWorkspaceFormatter,
              editable: false,
              options: { attributeConfig: lotAttributeConfig, valuesPath: 'stockLot.lotAttributeValues' },
              getRowMetaData: getLotAttributeRowMetaData,
            };
          })
        : [];

    const precision = selectedNode?.precision || 0;

    return [
      {
        name: 'Bin Location',
        key: 'stockLot.stock.binLocation.location',
        editable: false,
      },
      ...attributeColumns,
      {
        name: 'Free Stock',
        key: 'stockLot.freeStock',
        formatter: 'numeric',
        editable: false,
        options: {
          suppressZeroes: false,
          format: {
            decimals: precision,
          },
        },
      },
      {
        name: 'Reserved',
        key: 'reservation.quantity',
        formatter: 'numeric',
        editable: false,
        options: {
          format: {
            decimals: precision,
          },
        },
      },
      {
        name: (
          <span>
            To Reserve
            <HintIcon hint={'Enter a negative number to un-reserve stock'} />
          </span>
        ),
        key: 'quantity',
        formatter: ReservationQtyFormatter,
        editable: (rowData: IAssemblyReservationDetail) => {
          return rowData?.stockLot?.freeStock > 0.0 || rowData?.reservation?.quantity > 0.0;
        },
        options: {
          formatterOptions: {
            reserveAll,
            selectedComponent,
            assemblyReservations,
          },
          format: {
            decimals: precision,
          },
        },
        tooltip: 'Enter a negative number to un-reserve stock',
      },
    ];
  }, [assemblyReservations, reserveAll, selectedComponent]);

  const [fetchAssembly] = useLazyQuery<IAssemblyQueryResult, IAssemblyQueryVariables>(Assembly.instance!, {
    variables: {
      id: assemblyId,
      startingRowPath: '1',
    },
    fetchPolicy: 'network-only',
  });

  const assemblyReservationDetails = useMemo(() => {
    const filtered = assemblyReservations?.[selectedComponent?.rowPath!]?.assemblyReservationDetails?.filter(
      (assemblyReservationDetail) => {
        return assemblyReservationDetail?.reservation || assemblyReservationDetail?.stockLot?.freeStock > 0.0;
      }
    );

    // Group by stockLot and sum reservation.quantity
    return _.chain(filtered)
      .groupBy('stockLot.id')
      .map((details) => {
        return {
          ...details[0],
          reservation: {
            ...details[0]!.reservation,
            quantity: _.sumBy(details, 'reservation.quantity'),
          },
        };
      })
      .value();
  }, [assemblyReservations]);

  const completeReservation = async () => {
    setReserveAll(false);
    printDocumentSingle(ReportType.AssemblyPickingList, assembly.id, {
      metaData: { source: AssemblyPrintSource.Assembly },
    });
    updateAssemblyReservations(null);
    await fetchAssemblyReservations({
      variables: {
        assemblyId: selectedNode?.assembly?.id ?? assembly.id,
        productComponentId: nodeId,
      },
    });
    fetchAssembly().then(({ data }) => {
      if (data?.data) {
        setAssembly(data.data);
        refetchAssemblyComponentInformation();
      }
    });
  };

  const actions = useAssemblyReservationFormActions(
    assemblyReservations ? Object.values(assemblyReservations!) : [],
    reserveAllHandler,
    completeReservation
  );

  const selectedRowIndex = selectedComponent?.rowPath!;

  useEffect(() => {
    const reservations = assemblyReservations ? Object.values(assemblyReservations!) : [];
    const hasUnprocessedReservations =
      !reserveAll &&
      !reservations
        ?.flatMap((assemblyReservation) => assemblyReservation?.assemblyReservationDetails)
        .some(
          (assemblyReservationDetail) =>
            (assemblyReservationDetail as ExtendedAssemblyReservationDetail)?.quantity !== undefined &&
            parseFloat((assemblyReservationDetail as ExtendedAssemblyReservationDetail).quantity as any) !== 0.0
        );

    setAllowNavigation(hasUnprocessedReservations);
  }, [reserveAll, assemblyReservations]);

  const cancelPrompt = () => {
    return (
      <>
        <Prompt
          message={(location, action) => {
            if (!allowNavigation) {
              setCancelModalOpen(true);
              return false;
            }
            return true;
          }}
        />
        <Dialog
          hidden={!cancelModalOpen}
          dialogContentProps={{
            type: DialogType.normal,
            title: `Leave without processing reservations`,
            closeButtonAriaLabel: 'Navigate',
            subText:
              "You have some reservations which you haven't processed, if you leave now your changes will be lost.",
          }}
          minWidth={'450px'}
        >
          <DialogBottomButtonContainer>
            <Button
              data-testid="allow-navigation-cancel"
              onClick={() => {
                setCancelModalOpen(false);
              }}
            >
              Stay here
            </Button>
            <SuccessButton
              data-testid="allow-navigation-confirm"
              onClick={() => {
                setAllowNavigation(true);
                setCancelModalOpen(false);
                history.goBack();
              }}
            >
              Leave without saving
            </SuccessButton>
          </DialogBottomButtonContainer>
        </Dialog>
      </>
    );
  };

  const assemblyTabFilters = () => {
    if (
      !selectedComponentInformation ||
      selectedComponentInformation?.component.productClass !== ProductClass.Assembly
    ) {
      return [];
    }

    if (assignedAssembly) {
      return [
        {
          filterOperation: FilterOperation.Equals,
          filterParameters: {
            field: 'id',
            value: assignedAssembly.id,
          },
        },
      ];
    }

    return [
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'product.id',
          value: selectedComponentInformation?.component.id,
        },
      },
      {
        filterOperation: FilterOperation.Empty,
        filterParameters: {
          field: 'assignments',
        },
      },
    ];
  };

  const renderReservationGrid = useMemo(() => {
    if (selectedNode.assembly) {
      return !loading ? (
        <EditableDataGrid
          columns={ASSEMBLY_RESERVATION_COLUMNS}
          listData={assemblyReservationDetails || []}
          rowsCount={assemblyReservationDetails?.length}
          onRowUpdated={({ original, updated, cellKey }) => {
            if (cellKey === 'quantity') {
              const updatedReservation = assemblyReservations?.[selectedRowIndex];
              // find original index of the reservation based on stock lot as we have filtered out the displayed results
              const index = updatedReservation?.assemblyReservationDetails?.findIndex((ard) => {
                return ard?.stockLot?.id === original?.stockLot.id;
              });
              updateAssemblyReservations({
                ...assemblyReservations,
                [selectedRowIndex]: setIn(
                  updatedReservation,
                  `assemblyReservationDetails.${index}.quantity`,
                  updated![cellKey]
                ),
              });
            }
          }}
          enableCellSelect
          screenName="assembly-reservation-reservations"
        />
      ) : (
        <Spinner />
      );
    } else {
      return (
        <EditableDataGrid columns={ASSEMBLY_RESERVATION_COLUMNS} emptyRowsView={ReservationDataGridEmptyComponent} />
      );
    }
  }, [selectedNode, assemblyReservations]);

  return (
    <>
      {cancelPrompt()}
      <Form.FormContainer>
        <HeaderBlock
          name={`Component Reservation - ${assembly?.product?.reference}`}
          customBreadcrumb={[
            { path: '/menu/Products', title: 'Products' },
            { path: '/menu/Products/Assembly', title: 'Assembly' },
          ]}
          subtitle={assembly?.orderNumber}
          readOnly={true}
          history={history}
          actionButtons={actions}
          onCancel={() => {
            history.goBack();
          }}
        >
          <BlurTextField
            data-testid="assembly-product-reference"
            value={assembly?.product?.reference ?? ''}
            label={'Product Ref'}
            spellCheck="false"
            readOnly={true}
          />
        </HeaderBlock>
        {loading || !selectedComponentInformation ? (
          <Spinner />
        ) : (
          <Form.Form>
            <Form.Row>
              <Form.Fieldset cols={1}>
                <Form.Legend label="Component Information" />
                <Form.Row>
                  <Form.Fieldset cols={5 / 16}>
                    <Label>Product Ref</Label>
                  </Form.Fieldset>
                  <Form.Fieldset cols={3 / 16}>
                    <Link
                      to={`/Products/Product/Details/${selectedComponentInformation?.component?.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {selectedComponentInformation?.component?.reference ?? 'UNDEFINED REF'}
                    </Link>
                  </Form.Fieldset>
                  <Form.Fieldset cols={5 / 16}>
                    <Label>Reserved</Label>
                  </Form.Fieldset>
                  <Form.Fieldset cols={3 / 16}>
                    <BlurTextField
                      data-testid="reserved"
                      value={selectedComponentInformation?.reserved ?? 0}
                      decimals={selectedComponentInformation?.component?.precision ?? 0}
                      readOnly={true}
                      includeLabel={false}
                    />
                  </Form.Fieldset>
                </Form.Row>
                <Form.Row>
                  <Form.Fieldset cols={5 / 16}>
                    <Label>Description</Label>
                  </Form.Fieldset>
                  <Form.Fieldset cols={3 / 16}>
                    <BlurTextField
                      data-testid="description"
                      value={selectedComponentInformation?.component?.description ?? ''}
                      spellCheck="false"
                      readOnly={true}
                      includeLabel={false}
                    />
                  </Form.Fieldset>
                  <Form.Fieldset cols={5 / 16}>
                    <Label>Received</Label>
                  </Form.Fieldset>
                  <Form.Fieldset cols={3 / 16}>
                    <BlurTextField
                      data-testid="received"
                      value={selectedComponentInformation?.received ?? 0}
                      decimals={selectedComponentInformation?.component?.precision ?? 0}
                      spellCheck="false"
                      readOnly={true}
                      includeLabel={false}
                      type={BlurTextFieldType.Text}
                    />
                  </Form.Fieldset>
                </Form.Row>
                <Form.Row>
                  <Form.Fieldset cols={5 / 16}>
                    <Label>Qty Per Assembly</Label>
                  </Form.Fieldset>
                  <Form.Fieldset cols={3 / 16}>
                    <BlurTextField
                      data-testid="qty-per-assembly"
                      value={selectedComponentInformation?.quantity ?? 1}
                      decimals={selectedComponentInformation?.component?.precision ?? 0}
                      spellCheck="false"
                      readOnly={true}
                      includeLabel={false}
                    />
                  </Form.Fieldset>
                  <Form.Fieldset cols={5 / 16}>
                    <Label>Assigned</Label>
                  </Form.Fieldset>
                  <Form.Fieldset cols={3 / 16}>
                    <BlurTextField
                      data-testid="received"
                      value={selectedComponentInformation?.assigned}
                      decimals={selectedComponentInformation?.component?.precision ?? 0}
                      spellCheck="false"
                      readOnly={true}
                      includeLabel={false}
                    />
                  </Form.Fieldset>
                </Form.Row>
                <Form.Row>
                  <Form.Fieldset cols={5 / 16}>
                    <Label>Total Requirement</Label>
                  </Form.Fieldset>
                  <Form.Fieldset cols={3 / 16}>
                    <BlurTextField
                      data-testid="total-requirement"
                      value={selectedComponentInformation?.requiredQuantity}
                      decimals={selectedComponentInformation?.component?.precision ?? 0}
                      spellCheck="false"
                      readOnly={true}
                      includeLabel={false}
                    />
                  </Form.Fieldset>
                  <Form.Fieldset cols={5 / 16}>
                    <Label>To Reserve</Label>
                  </Form.Fieldset>
                  <Form.Fieldset cols={3 / 16}>
                    <BlurTextField
                      data-testid="to-reserve"
                      value={
                        selectedComponentInformation?.requiredQuantity -
                        selectedComponentInformation?.reserved -
                        selectedComponentInformation?.received
                      }
                      decimals={selectedComponentInformation?.component?.precision ?? 0}
                      spellCheck="false"
                      readOnly={true}
                      includeLabel={false}
                    />
                  </Form.Fieldset>
                </Form.Row>
              </Form.Fieldset>
            </Form.Row>
            <Form.TabPanel>
              <Form.TabContent id="reservations" label="Reservations">
                {renderReservationGrid}
              </Form.TabContent>
              <Form.TabContent
                id="assemblies"
                label="Assemblies"
                shouldRender={
                  !!selectedNode.assembly &&
                  selectedComponentInformation?.component.productClass === ProductClass.Assembly
                }
              >
                <LazyAssemblyWorkspace
                  viewScreenName="assemblies"
                  embeddedView
                  editInNewTab
                  allowView={false}
                  predefinedFilters={assemblyTabFilters()}
                />
              </Form.TabContent>
            </Form.TabPanel>
          </Form.Form>
        )}
      </Form.FormContainer>
    </>
  );
};

export default AssemblyReservationForm;
